import React, { useEffect, useState, useMemo, useCallback } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import Select from 'react-select';

const convertibleStatuses = new Set(['pending', 'confirmed', 'active']);

const getConversionBlockReason = (subscription) => {
  const status = String(subscription?.status || '').trim().toLowerCase();
  if (!convertibleStatuses.has(status)) {
    return `Status „${subscription?.status || '-'}“ ist nicht für die Auftragserstellung freigegeben.`;
  }

  if (String(subscription?.subscriptionType || '').trim() !== 'standard') {
    return 'Nur Standard-Subscriptions können direkt in Aufträge umgewandelt werden.';
  }

  if (subscription?.endDate && subscription?.nextDueDate) {
    const endDate = new Date(subscription.endDate);
    const dueDate = new Date(subscription.nextDueDate);
    endDate.setHours(23, 59, 59, 999);
    if (!Number.isNaN(endDate.getTime()) && !Number.isNaN(dueDate.getTime()) && dueDate > endDate) {
      return 'Die Fälligkeit liegt nach dem Ende der Subscription.';
    }
  }

  return null;
};

const SubscriptionListDueDate = () => {
  // Daten
  const [subs, setSubs] = useState([]);

  // Filteroptionen vom Server
  const [serverFilterOptions, setServerFilterOptions] = useState({
    subscriptionNumbers: [],
    referenceNumbers: [],
    customers: [],
    contacts: [],
    startDates: [],
    statuses: [],
    subscriptionTypes: [],
  });

  // Pagination – MUSS vor Effekten deklariert sein, die setCurrentPage nutzen
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 25;

  // Filter (persistiert)
  const [filters, setFilters] = useState(() => {
    const saved = localStorage.getItem('subscriptionDueFilters');
    return saved
      ? JSON.parse(saved)
      : {
          subscriptionNumber: null,
          referenceNumber: null,
          customer: null,
          contact: null,
          startDate: null,
          status: null,
          subscriptionType: null,
        };
  });

  // AUSWAHL – MUSS vor abgeleiteten Werten deklariert sein
  const [selectedSubscriptions, setSelectedSubscriptions] = useState(() => new Map());
  const [isCreatingOrders, setIsCreatingOrders] = useState(false);
  const [conversionReport, setConversionReport] = useState(null);

  // Abgeleitete Werte (aus States)
  const selectedIds = useMemo(() => Array.from(selectedSubscriptions.keys()), [selectedSubscriptions]);
  const selectablePageSubscriptions = useMemo(
    () => subs.filter((subscription) => !getConversionBlockReason(subscription)),
    [subs]
  );
  const pageIds = useMemo(
    () => selectablePageSubscriptions.map((subscription) => subscription.id),
    [selectablePageSubscriptions]
  );
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedSubscriptions.has(id));

  const toggleSelectPage = useCallback((checked) => {
    setSelectedSubscriptions((previous) => {
      const next = new Map(previous);
      selectablePageSubscriptions.forEach((subscription) => {
        if (checked) {
          next.set(subscription.id, subscription.nextDueDate);
        } else {
          next.delete(subscription.id);
        }
      });
      return next;
    });
  }, [selectablePageSubscriptions]);

  // Filter persistieren & bei Änderung auf Seite 1 springen
  useEffect(() => {
    localStorage.setItem('subscriptionDueFilters', JSON.stringify(filters));
    setCurrentPage(1);
    setSelectedSubscriptions(new Map());
    setConversionReport(null);
  }, [filters]);

  // Filteroptionen laden (Due-Variante)
  useEffect(() => {
    const fetchOpts = async () => {
      try {
        const resp = await api.get('/subscriptions/due/filter-options');
        setServerFilterOptions(resp.data);
      } catch (e) {
        console.error('Fehler beim Laden der Due-Filteroptionen:', e);
      }
    };
    fetchOpts();
  }, []);

  // Query für /due/list
  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.append('limit', limit);
    params.append('page', currentPage);
    if (filters.subscriptionNumber) params.append('subscriptionNumber', filters.subscriptionNumber.value);
    if (filters.referenceNumber) params.append('referenceNumber', filters.referenceNumber.value);
    if (filters.customer) params.append('customer', filters.customer.value);
    if (filters.contact) params.append('contact', filters.contact.value);
    if (filters.startDate) params.append('startDate', filters.startDate.value);
    if (filters.status) params.append('status', filters.status.value);
    if (filters.subscriptionType) params.append('subscriptionType', filters.subscriptionType.value);
    return `?${params.toString()}`;
  }, [currentPage, filters]);

  // Daten laden (abhängig von Filtern & Seite)
  const loadSubscriptions = useCallback(async () => {
    try {
      const query = buildQuery();
      const resp = await api.get(`/subscriptions/due/list${query}`);
      const pages = Math.max(1, Number(resp.data.pages) || 1);
      setTotalPages(pages);

      if (currentPage > pages) {
        setSubs([]);
        setCurrentPage(pages);
        return;
      }

      setSubs(resp.data.data || []);
    } catch (e) {
      console.error('Fehler beim Abrufen der Due-Subscriptions:', e);
    }
  }, [buildQuery, currentPage]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  };

  // Optionen für react-select
  const optionsSubscriptionNumber = serverFilterOptions.subscriptionNumbers.map(v => ({ value: v, label: v }));
  const optionsReferenceNumber = serverFilterOptions.referenceNumbers.map(v => ({ value: v, label: v }));
  const optionsCustomer = serverFilterOptions.customers.map(v => ({ value: v, label: v }));
  const optionsContact = serverFilterOptions.contacts.map(v => ({ value: v, label: v }));
  const optionsStartDate = serverFilterOptions.startDates.map(v => ({ value: v, label: v }));
  const optionsStatus = serverFilterOptions.statuses.map(v => ({ value: v, label: v }));
  const optionsSubscriptionType = serverFilterOptions.subscriptionTypes.map(v => ({ value: v, label: v }));

  // Bulk-Erstellung
  const createOrdersFromSubscriptions = async () => {
    if (selectedIds.length === 0) {
      alert('Bitte wählen Sie mindestens eine Subscription aus.');
      return;
    }

    if (isCreatingOrders) return;

    const subscriptions = selectedIds.map((id) => ({
      id,
      expectedDueDate: selectedSubscriptions.get(id),
    }));

    setIsCreatingOrders(true);
    setConversionReport(null);

    try {
      const response = await api.post('/subscriptions/due/duplicateToOrders', { subscriptions });
      setConversionReport(response.data);
      setSelectedSubscriptions(new Map());
    } catch (e) {
      console.error('Fehler beim Erstellen der Aufträge:', e);
      setConversionReport({
        summary: { requested: selectedIds.length, created: 0, reused: 0, failed: selectedIds.length },
        results: [],
        requestError: e.response?.data?.error || 'Die Aufträge konnten nicht verarbeitet werden.',
      });
    } finally {
      await loadSubscriptions();
      setIsCreatingOrders(false);
    }
  };

  const resetFilters = () => {
    setFilters({
      subscriptionNumber: null,
      referenceNumber: null,
      customer: null,
      contact: null,
      startDate: null,
      status: null,
      subscriptionType: null,
    });
  };

  return (
    <div className="subscription-due-list">
      <Helmet>
        <title>Fällige Subscriptions</title>
        <meta name="description" content="Liste fälliger Subscriptions" />
      </Helmet>

      <h1>Fällige Subscriptions</h1>

      {/* Filterleiste */}
      <div style={{ marginBottom: '20px' }}>
        <h3>Filter</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px' }}>
          <Select
            options={optionsSubscriptionNumber}
            placeholder="Subscriptionnummer"
            isClearable
            value={filters.subscriptionNumber}
            onChange={(selected) => setFilters({ ...filters, subscriptionNumber: selected })}
          />
          <Select
            options={optionsReferenceNumber}
            placeholder="Referenz"
            isClearable
            value={filters.referenceNumber}
            onChange={(selected) => setFilters({ ...filters, referenceNumber: selected })}
          />
          <Select
            options={optionsCustomer}
            placeholder="Kunde"
            isClearable
            value={filters.customer}
            onChange={(selected) => setFilters({ ...filters, customer: selected })}
          />
          <Select
            options={optionsContact}
            placeholder="Besteller"
            isClearable
            value={filters.contact}
            onChange={(selected) => setFilters({ ...filters, contact: selected })}
          />
          <Select
            options={optionsStartDate}
            placeholder="Startdatum"
            isClearable
            value={filters.startDate}
            onChange={(selected) => setFilters({ ...filters, startDate: selected })}
          />
          <Select
            options={optionsStatus}
            placeholder="Status"
            isClearable
            value={filters.status}
            onChange={(selected) => setFilters({ ...filters, status: selected })}
          />
          <Select
            options={optionsSubscriptionType}
            placeholder="Subscription-Typ"
            isClearable
            value={filters.subscriptionType}
            onChange={(selected) => setFilters({ ...filters, subscriptionType: selected })}
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <button onClick={resetFilters}>Filter zurücksetzen</button>
        </div>
      </div>

      {/* Aktionen für Mehrfachauswahl */}
      <div style={{ margin: '10px 0', display: 'flex', gap: 10, alignItems: 'center' }}>
        <button
          onClick={async () => {
            try {
              const params = new URLSearchParams();
              if (filters.subscriptionNumber) params.append('subscriptionNumber', filters.subscriptionNumber.value);
              if (filters.referenceNumber) params.append('referenceNumber', filters.referenceNumber.value);
              if (filters.customer) params.append('customer', filters.customer.value);
              if (filters.contact) params.append('contact', filters.contact.value);
              if (filters.startDate) params.append('startDate', filters.startDate.value);
              if (filters.status) params.append('status', filters.status.value);
              if (filters.subscriptionType) params.append('subscriptionType', filters.subscriptionType.value);

              const resp = await api.get(`/subscriptions/due/ids?${params.toString()}`);
              const entries = Array.isArray(resp.data.entries) ? resp.data.entries : [];
              setSelectedSubscriptions(new Map(
                entries.map((entry) => [Number(entry.id), entry.nextDueDate])
              ));
            } catch (e) {
              console.error('Fehler beim Laden aller IDs:', e);
              alert('IDs konnten nicht geladen werden.');
            }
          }}
          disabled={isCreatingOrders}
        >
          Alle gefilterten auswählen
        </button>

        <button onClick={() => toggleSelectPage(true)} disabled={isCreatingOrders || pageIds.length === 0}>
          Alle auf dieser Seite auswählen
        </button>
        <button onClick={() => setSelectedSubscriptions(new Map())} disabled={isCreatingOrders || selectedIds.length === 0}>
          Auswahl leeren
        </button>

        <span style={{ opacity: 0.7 }}>Ausgewählt: {selectedIds.length}</span>
      </div>

      {/* Tabelle */}
      {subs.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #ccc', textAlign: 'left' }}>
              <th style={{ padding: 8 }}>
                <input
                  type="checkbox"
                  checked={allPageSelected}
                  onChange={(e) => toggleSelectPage(e.target.checked)}
                  disabled={isCreatingOrders || pageIds.length === 0}
                  title="Alle auf dieser Seite auswählen"
                />
              </th>
              <th style={{ padding: 8 }}>ID</th>
              <th style={{ padding: 8 }}>Subscriptionnummer</th>
              <th style={{ padding: 8 }}>Referenz</th>
              <th style={{ padding: 8 }}>Kunde</th>
              <th style={{ padding: 8 }}>Besteller</th>
              <th style={{ padding: 8 }}>Startdatum</th>
              <th style={{ padding: 8 }}>Nächste Fälligkeit</th>
              <th style={{ padding: 8 }}>Status</th>
              <th style={{ padding: 8 }}>Typ</th>
              <th style={{ padding: 8 }}>Gesamt netto (€)</th>
              <th style={{ padding: 8 }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {subs.map(sub => {
              const customerName =
                sub.customer?.company ||
                `${sub.customer?.firstName || ''} ${sub.customer?.lastName || ''}`.trim() ||
                'Unbekannt';
              const contactName = sub.contact
                ? `${sub.contact.contactFirstName || ''} ${sub.contact.contactLastName || ''}`.trim()
                : '';
              const conversionBlockReason = getConversionBlockReason(sub);

              return (
                <tr
                  key={sub.id}
                  style={{ borderBottom: '1px solid #eee', opacity: conversionBlockReason ? 0.65 : 1 }}
                >
                  <td style={{ padding: 8 }}>
                    <input
                      type="checkbox"
                      checked={selectedSubscriptions.has(sub.id)}
                      disabled={isCreatingOrders || Boolean(conversionBlockReason)}
                      title={conversionBlockReason || 'Subscription auswählen'}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setSelectedSubscriptions((previous) => {
                          const next = new Map(previous);
                          if (checked) {
                            next.set(sub.id, sub.nextDueDate);
                          } else {
                            next.delete(sub.id);
                          }
                          return next;
                        });
                      }}
                    />
                  </td>
                  <td style={{ padding: 8 }}>{sub.id}</td>
                  <td style={{ padding: 8 }}>{sub.subscriptionNumber || '-'}</td>
                  <td style={{ padding: 8 }}>{sub.referenceNumber || '-'}</td>
                  <td style={{ padding: 8 }}>{customerName}</td>
                  <td style={{ padding: 8 }}>{contactName}</td>
                  <td style={{ padding: 8 }}>{formatDate(sub.startDate)}</td>
                  <td style={{ padding: 8 }}>{formatDate(sub.nextDueDate)}</td>
                  <td style={{ padding: 8 }}>{sub.status}</td>
                  <td style={{ padding: 8 }}>
                    <div>{sub.subscriptionType || '-'}</div>
                    {conversionBlockReason && (
                      <small style={{ color: '#b42318' }}>{conversionBlockReason}</small>
                    )}
                  </td>
                  <td style={{ padding: 8 }}>
                    {(sub.totalNet && sub.totalNet.toFixed(2).replace('.', ',')) || '0,00'} €
                  </td>
                  <td style={{ padding: 8 }}>
                    <button
                      onClick={() => (window.location.href = `/subscriptions/${sub.id}/edit`)}
                      style={{
                        padding: '5px 10px',
                        background: '#007bff',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 3,
                        cursor: 'pointer'
                      }}
                    >
                      Bearbeiten
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p>Keine fälligen Subscriptions gefunden.</p>
      )}

      {/* Pagination */}
      <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
        <button
          onClick={() => setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)}
          disabled={currentPage === 1}
          style={{ padding: '8px 12px' }}
        >
          Vorherige
        </button>
        <span>Seite {currentPage} von {totalPages}</span>
        <button
          onClick={() => setCurrentPage(currentPage < totalPages ? currentPage + 1 : totalPages)}
          disabled={currentPage === totalPages}
          style={{ padding: '8px 12px' }}
        >
          Nächste
        </button>
      </div>

      {/* Bulk-Button */}
      <div style={{ marginTop: 20 }}>
        <button
          onClick={createOrdersFromSubscriptions}
          disabled={isCreatingOrders || selectedIds.length === 0}
          style={{
            padding: '10px 20px',
            background: (isCreatingOrders || selectedIds.length === 0) ? '#aaa' : '#28a745',
            color: '#fff',
            border: 'none',
            borderRadius: 5,
            cursor: isCreatingOrders ? 'wait' : (selectedIds.length === 0 ? 'not-allowed' : 'pointer')
          }}
        >
          {isCreatingOrders ? 'Aufträge werden erstellt …' : 'Ausgewählte → Aufträge erstellen'}
        </button>
      </div>

      {conversionReport && (
        <div
          role="status"
          style={{
            marginTop: 16,
            padding: 14,
            border: `1px solid ${conversionReport.summary?.failed ? '#dc3545' : '#28a745'}`,
            borderRadius: 6,
            background: conversionReport.summary?.failed ? '#fff5f5' : '#f3fff6',
          }}
        >
          <strong>Ergebnis der Auftragserstellung</strong>
          {conversionReport.requestError ? (
            <p style={{ color: '#b42318', marginBottom: 0 }}>{conversionReport.requestError}</p>
          ) : (
            <>
              <p>
                Neu erstellt: {conversionReport.summary?.created || 0} · Bereits vorhanden: {conversionReport.summary?.reused || 0}
                {' '}· Fehlgeschlagen: {conversionReport.summary?.failed || 0}
              </p>

              {(conversionReport.results || []).some((result) => result.success) && (
                <ul>
                  {(conversionReport.results || []).filter((result) => result.success).map((result) => (
                    <li key={`success-${result.subscriptionId}`}>
                      {result.subscriptionNumber || `Subscription #${result.subscriptionId}`}: {' '}
                      <a href={result.editUrl}>{result.orderNumber || `Auftrag #${result.orderId}`}</a>
                      {result.created ? ' erstellt' : ' war bereits vorhanden'}
                    </li>
                  ))}
                </ul>
              )}

              {(conversionReport.results || []).some((result) => !result.success) && (
                <ul style={{ color: '#b42318' }}>
                  {(conversionReport.results || []).filter((result) => !result.success).map((result) => (
                    <li key={`failure-${result.subscriptionId}`}>
                      Subscription #{result.subscriptionId}: {result.error}
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionListDueDate;
